import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { Reveal } from "@/components/motion/Reveal";
import { Button, Eyebrow } from "@/components/ui-kit";
import { CTASection } from "@/components/CTASection";
import { CASE_STUDIES } from "@/data/content";
import { POINT_OF_VIEW, SITUATIONS, SERVICES, WHY_FRACTIONAL, WHY_PMN, EXPERIENCE, ATTRIBUTION } from "@/data/site";

const H2 = ({ children, dark = false, className = "" }) => (
  <h2 className={`mt-6 text-3xl font-normal leading-[1.15] tracking-tightish sm:text-4xl lg:text-5xl ${dark ? "text-paper" : "text-charcoal"} ${className}`}>
    {children}
  </h2>
);

function PointOfView() {
  return (
    <section className="bg-paper py-24 lg:py-32" data-testid="home-pov">
      <div className="mx-auto grid max-w-8xl gap-10 px-6 lg:grid-cols-12 lg:px-10">
        <Reveal className="lg:col-span-7">
          <Eyebrow>{POINT_OF_VIEW.eyebrow}</Eyebrow>
          <H2 className="max-w-2xl">{POINT_OF_VIEW.title}</H2>
        </Reveal>
        <Reveal delay={0.1} className="space-y-6 text-base leading-[1.8] text-charcoal/70 lg:col-span-5 lg:pt-14">
          {POINT_OF_VIEW.paragraphs.map((p) => <p key={p.slice(0, 20)}>{p}</p>)}
        </Reveal>
      </div>
    </section>
  );
}

function Situations() {
  return (
    <section className="bg-paper-warm py-24 lg:py-32" data-testid="home-situations">
      <div className="mx-auto max-w-8xl px-6 lg:px-10">
        <Reveal>
          <Eyebrow>{SITUATIONS.eyebrow}</Eyebrow>
          <H2 className="max-w-2xl">{SITUATIONS.title}</H2>
        </Reveal>
        <ol className="mt-16 grid gap-px overflow-hidden border border-charcoal/10 bg-charcoal/10 md:grid-cols-2 lg:grid-cols-3">
          {SITUATIONS.items.map((s, i) => (
            <Reveal as="li" key={s} delay={i * 0.06} className="flex h-full gap-5 bg-paper-warm p-8 lg:p-10">
              <span className="font-serif text-2xl text-teal" aria-hidden="true">{String(i + 1).padStart(2, "0")}</span>
              <p className="text-base leading-[1.75] text-charcoal/80">{s}</p>
            </Reveal>
          ))}
        </ol>
      </div>
    </section>
  );
}

function SelectedEngagements() {
  return (
    <section className="bg-charcoal py-24 text-paper lg:py-32" data-testid="home-engagements">
      <div className="mx-auto max-w-8xl px-6 lg:px-10">
        <div className="grid gap-8 lg:grid-cols-12 lg:items-end">
          <Reveal className="lg:col-span-8">
            <Eyebrow dark>Selected engagements</Eyebrow>
            <H2 dark className="max-w-2xl">Programs led, stabilized, and shipped.</H2>
            <p className="mt-6 max-w-2xl text-sm leading-[1.8] text-paper/60" data-testid="home-engagements-attribution">
              {ATTRIBUTION} Client identities are anonymized.
            </p>
          </Reveal>
          <Reveal delay={0.1} className="lg:col-span-4 lg:text-right">
            <Link to="/case-studies" className="inline-flex items-center gap-2 text-sm tracking-wideish text-teal-light link-underline" data-testid="home-case-studies-link">
              All case studies <ArrowUpRight className="h-4 w-4" strokeWidth={1.5} aria-hidden="true" />
            </Link>
          </Reveal>
        </div>
        <div className="mt-14 grid gap-px overflow-hidden border border-paper/15 bg-paper/15 md:grid-cols-2">
          {CASE_STUDIES.map((c, i) => (
            <Reveal key={c.title} delay={i * 0.06} className="bg-charcoal">
              <article className="flex h-full flex-col p-8 lg:p-10">
                <p className="text-xs uppercase tracking-mega text-teal-light">{c.sector}</p>
                <h3 className="mt-4 font-serif text-2xl leading-[1.25] tracking-tightish text-paper">{c.title}</h3>
                <dl className="mt-8 grid grid-cols-3 gap-4 border-t border-paper/10 pt-6">
                  {c.metrics.map(([v, l]) => (
                    <div key={l}>
                      <dt className="sr-only">{l}</dt>
                      <dd className="font-serif text-2xl text-paper">{v}</dd>
                      <dd className="mt-1 text-xs leading-snug text-paper/55">{l}</dd>
                    </div>
                  ))}
                </dl>
              </article>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function WhatPmnDoes() {
  return (
    <section className="bg-paper py-24 lg:py-32" data-testid="home-services">
      <div className="mx-auto max-w-8xl px-6 lg:px-10">
        <Reveal>
          <Eyebrow>What PMN does</Eyebrow>
          <H2 className="max-w-2xl">Three ways to engage.</H2>
        </Reveal>
        <div className="mt-16 grid gap-px overflow-hidden border border-charcoal/10 bg-charcoal/10 lg:grid-cols-3">
          {SERVICES.map((s, i) => (
            <Reveal key={s.id} delay={i * 0.08} className="bg-paper">
              <article className="flex h-full flex-col p-9 lg:p-11">
                <span className="font-serif text-3xl text-teal" aria-hidden="true">{s.n}</span>
                <h3 className="mt-5 text-2xl font-normal tracking-tightish text-charcoal">{s.title}</h3>
                <p className="mt-4 flex-1 text-base leading-[1.8] text-charcoal/70">{s.summary}</p>
                <Link to={`/services#${s.id}`} className="mt-8 inline-flex items-center gap-2 text-sm tracking-wideish text-teal link-underline" data-testid={`home-service-${s.id}`}>
                  More on {s.title} <ArrowUpRight className="h-4 w-4" strokeWidth={1.5} aria-hidden="true" />
                </Link>
              </article>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function WhyFractional() {
  return (
    <section className="bg-paper-warm py-24 lg:py-32" data-testid="home-why-fractional">
      <div className="mx-auto max-w-8xl px-6 lg:px-10">
        <div className="grid gap-10 lg:grid-cols-12">
          <Reveal className="lg:col-span-6">
            <Eyebrow>{WHY_FRACTIONAL.eyebrow}</Eyebrow>
            <H2 className="max-w-xl">{WHY_FRACTIONAL.title}</H2>
          </Reveal>
          <Reveal delay={0.1} className="lg:col-span-6 lg:pt-14">
            <p className="text-base leading-[1.8] text-charcoal/70">{WHY_FRACTIONAL.body}</p>
          </Reveal>
        </div>
        <div className="mt-16 grid gap-10 border-t border-charcoal/10 pt-12 md:grid-cols-3">
          {WHY_FRACTIONAL.pillars.map((p, i) => (
            <Reveal key={p.title} delay={i * 0.08}>
              <h3 className="text-lg font-medium tracking-tightish text-charcoal">{p.title}</h3>
              <p className="mt-3 text-sm leading-[1.8] text-charcoal/65">{p.body}</p>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function WhyPmn() {
  return (
    <section className="bg-paper py-24 lg:py-32" data-testid="home-why-pmn">
      <div className="mx-auto max-w-8xl px-6 lg:px-10">
        <Reveal>
          <Eyebrow>{WHY_PMN.eyebrow}</Eyebrow>
          <H2 className="max-w-2xl">{WHY_PMN.title}</H2>
        </Reveal>
        <div className="mt-16 grid gap-px overflow-hidden border border-charcoal/10 bg-charcoal/10 md:grid-cols-2">
          {WHY_PMN.items.map((it, i) => (
            <Reveal key={it.title} delay={i * 0.08} className="bg-paper">
              <div className="h-full p-9 lg:p-11">
                <span className="block h-2 w-2 rotate-45 bg-teal" aria-hidden="true" />
                <h3 className="mt-6 text-xl font-normal tracking-tightish text-charcoal">{it.title}</h3>
                <p className="mt-4 text-sm leading-[1.8] text-charcoal/65">{it.body}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function Experience() {
  return (
    <section className="bg-charcoal py-24 text-paper lg:py-32" data-testid="home-experience">
      <div className="mx-auto max-w-8xl px-6 lg:px-10">
        <Reveal>
          <Eyebrow dark>{EXPERIENCE.eyebrow}</Eyebrow>
          <H2 dark className="max-w-2xl">{EXPERIENCE.title}</H2>
          <p className="mt-6 max-w-2xl text-sm leading-[1.8] text-paper/60" data-testid="home-experience-attribution">{EXPERIENCE.attribution}</p>
        </Reveal>
        <div className="mt-14 grid gap-px overflow-hidden border border-paper/15 bg-paper/15 sm:grid-cols-2 lg:grid-cols-4">
          {EXPERIENCE.stats.map((s, i) => (
            <Reveal key={s.label} delay={i * 0.08} className="bg-charcoal">
              <div className="h-full p-9 lg:p-10">
                <p className="font-serif text-5xl tracking-tightish text-paper lg:text-6xl">{s.value}</p>
                <p className="mt-4 text-sm leading-[1.7] text-paper/60">{s.label}</p>
              </div>
            </Reveal>
          ))}
        </div>
        <Reveal delay={0.1}>
          <p className="mt-10 max-w-3xl text-sm leading-[1.8] text-paper/55">{EXPERIENCE.note}</p>
          <Button to="/about" variant="dark" className="mt-10" track="secondary" data-testid="home-about-btn">About the firm</Button>
        </Reveal>
      </div>
    </section>
  );
}

// Approved homepage sequence after the hero.
export default function HomeSections() {
  return (
    <>
      <PointOfView />
      <Situations />
      <SelectedEngagements />
      <WhatPmnDoes />
      <WhyFractional />
      <WhyPmn />
      <Experience />
      <CTASection
        eyebrow="Start a conversation"
        title="Have a critical initiative that needs senior leadership?"
        body="Tell us what's driving it and we'll tell you straight whether PMN is the right fit — and which engagement structure makes sense."
        image={null}
      />
    </>
  );
}
